import type HighchartsType from 'highcharts/highstock';

import { createFullScreenYAxisOptions } from './fullScreenChartAxis';
import { createFullScreenRangeSelectorOptions } from './fullScreenChartRangeSelector';
import { toHighchartsSeries } from './fullScreenChartSeries';
import { createFullScreenRolloverTooltip } from './fullScreenChartTooltip';
import { applyFullscreenWatermarkLayout } from './fullScreenChartWatermark';

import type { CreateFullScreenStockOptionsParams } from './fullScreenChartTypes';

const STOCK_TOOLS_BUTTONS = [
  'indicators',
  'simpleShapes',
  'lines',
  'crookedLines',
  'measure',
  'advanced',
  'toggleAnnotations',
  'verticalLabels',
  'flags',
  'zoomChange',
  'currentPriceIndicator'
] as const;

export const withoutInitialRangeSelection = (
  options: HighchartsType.Options
): HighchartsType.Options => {
  const rangeSelector = { ...options.rangeSelector };
  delete rangeSelector.selected;
  return { ...options, rangeSelector };
};

export const createFullScreenStockOptions = ({
  appearance,
  chartSeries,
  hiddenSeriesIds,
  prefersReducedMotion = false,
  tooltip = createFullScreenRolloverTooltip(() => '', appearance),
  useSmallWatermark = false
}: CreateFullScreenStockOptionsParams): HighchartsType.Options => ({
  accessibility: { enabled: false },
  chart: {
    allowMutatingData: false,
    animation: !prefersReducedMotion,
    backgroundColor: appearance.background,
    events: {
      load() {
        applyFullscreenWatermarkLayout(this, appearance, useSmallWatermark);
      },
      redraw() {
        applyFullscreenWatermarkLayout(this, appearance, useSmallWatermark);
      }
    },
    height: null,
    reflow: true,
    style: { color: appearance.foreground, fontFamily: appearance.fontFamily },
    type: 'line',
    zooming: { type: 'x' }
  },
  credits: { enabled: false },
  exporting: {
    buttons: { contextButton: { enabled: false } },
    enabled: true,
    fallbackToExportServer: false
  },
  legend: { enabled: false },
  lang: { rangeSelectorZoom: '' },
  navigator: {
    adaptToUpdatedData: false,
    enabled: true,
    handles: {
      backgroundColor: appearance.navigatorBackground,
      borderColor: appearance.border
    },
    maskFill: appearance.navigatorBackground,
    outlineColor: appearance.border,
    series: {
      animation: !prefersReducedMotion,
      type: 'areaspline'
    } as HighchartsType.SeriesAreasplineOptions
  },
  plotOptions: {
    line: { states: { hover: { lineWidth: 3 } } },
    series: {
      animation: !prefersReducedMotion,
      states: { hover: { enabled: false }, inactive: { opacity: 1 } }
    }
  },
  rangeSelector: createFullScreenRangeSelectorOptions(appearance, chartSeries),
  scrollbar: { enabled: true },
  series: chartSeries.map((series, index) =>
    toHighchartsSeries(series, index, hiddenSeriesIds, appearance)
  ),
  stockTools: { gui: { buttons: [...STOCK_TOOLS_BUTTONS], enabled: true } },
  title: { text: undefined },
  tooltip,
  xAxis: {
    crosshair: true,
    gridLineColor: appearance.grid,
    labels: { style: { color: appearance.foreground, fontSize: '10px' } },
    lineColor: appearance.grid,
    ordinal: false,
    tickColor: appearance.grid,
    type: 'datetime'
  },
  yAxis: createFullScreenYAxisOptions(appearance)
});
